import React, { useState, useEffect, useMemo } from "react";
import { ProposalMilestone, Opportunity, UserProfile, MilestoneCategory } from "../../types";
import {
  subscribeToMilestones,
  createMilestone,
  updateMilestone,
  deleteMilestone,
  toggleMilestoneCompleted,
  upsertAutoMilestones,
} from "../../services/milestonesService";
import {
  formatCountdown,
  getMilestoneUrgency,
  buildIcs,
  buildGoogleCalendarUrl,
} from "../../utils/calendarUtils";
import { MilestoneModal } from "./MilestoneModal";
import {
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  Plus,
  Sparkles,
  Download,
  ExternalLink,
  Edit2,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Loader2,
  User,
  Check,
} from "lucide-react";

interface MilestonesTimelineProps {
  opportunity: Opportunity;
  user: UserProfile | null;
}

export const MilestonesTimeline: React.FC<MilestonesTimelineProps> = ({ opportunity, user }) => {
  const [milestones, setMilestones] = useState<ProposalMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<ProposalMilestone | null>(null);

  // Auto-generation & Action states
  const [isGenerating, setIsGenerating] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copiedCal, setCopiedCal] = useState(false);

  // 1. Live subscription to milestones subcollection
  useEffect(() => {
    const unsub = subscribeToMilestones(
      opportunity.id,
      (items) => {
        setMilestones(items);
        setLoading(false);
      },
      (err) => {
        console.error("Error subscribing to milestones:", err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [opportunity.id]);

  // 2. 60-second timer to keep countdown and urgencies accurate
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Submission milestone check
  const submissionMilestone = useMemo(
    () => milestones.find((m) => m.autoKey === "submission" || m.category === "external_deadline"),
    [milestones],
  );

  const targetSubmissionDate = submissionMilestone?.targetDate || opportunity.dueDate;
  const targetSubmissionTime = submissionMilestone?.targetTime || "15:00";
  const countdown = useMemo(
    () => formatCountdown(targetSubmissionDate, targetSubmissionTime, "America/Chicago", now),
    [targetSubmissionDate, targetSubmissionTime, now],
  );

  // Progress calculations
  const totalCount = milestones.length;
  const completedCount = milestones.filter((m) => m.status === "completed").length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Next upcoming milestone
  const nextMilestone = useMemo(() => {
    return milestones.find((m) => m.status === "pending");
  }, [milestones]);

  // Detect if dueDate has drifted from auto_submission milestone
  const dueDateDrift = useMemo(() => {
    if (!opportunity.dueDate || !submissionMilestone) return false;
    return opportunity.dueDate !== submissionMilestone.targetDate;
  }, [opportunity.dueDate, submissionMilestone]);

  // Filtered milestones
  const filteredMilestones = useMemo(() => {
    return milestones.filter((m) => {
      if (categoryFilter !== "all" && m.category !== categoryFilter) return false;
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      return true;
    });
  }, [milestones, categoryFilter, statusFilter]);

  const handleGenerateSchedule = async () => {
    setIsGenerating(true);
    setActionError(null);
    try {
      await upsertAutoMilestones(opportunity);
    } catch (err) {
      console.error("Failed to generate schedule:", err);
      setActionError(err instanceof Error ? err.message : "Failed to generate schedule.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadIcs = () => {
    if (milestones.length === 0) return;
    const icsContent = buildIcs(milestones, opportunity.title, opportunity.client);
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `response-schedule-${opportunity.client.toLowerCase().replace(/\s+/g, "-")}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setCopiedCal(true);
    setTimeout(() => setCopiedCal(false), 2500);
  };

  const handleToggleComplete = async (m: ProposalMilestone) => {
    try {
      await toggleMilestoneCompleted(opportunity.id, m.id, m.status, user?.email);
    } catch (err) {
      console.error("Failed to toggle milestone:", err);
    }
  };

  const handleDelete = async (milestoneId: string) => {
    if (!window.confirm("Delete this milestone from the schedule?")) return;
    try {
      await deleteMilestone(opportunity.id, milestoneId);
    } catch (err) {
      console.error("Failed to delete milestone:", err);
    }
  };

  const handleSaveMilestone = async (data: Partial<ProposalMilestone>) => {
    if (editingMilestone) {
      await updateMilestone(opportunity.id, editingMilestone.id, data);
    } else {
      await createMilestone(opportunity.id, {
        title: data.title || "",
        description: data.description,
        targetDate: data.targetDate || opportunity.dueDate,
        targetTime: data.targetTime,
        timezone: data.timezone || "America/Chicago",
        category: data.category || "internal_gate",
        status: "pending",
        ownerEmail: data.ownerEmail,
        ownerName: data.ownerName,
        humanEdited: true,
      });
    }
  };

  const getCategoryBadge = (cat: MilestoneCategory) => {
    switch (cat) {
      case "external_deadline":
        return {
          label: "Client Deadline",
          color: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300",
        };
      case "internal_gate":
        return {
          label: "Internal Review Gate",
          color: "bg-teal-100 text-teal-800 dark:bg-teal-950/60 dark:text-teal-300",
        };
      case "client_event":
        return {
          label: "Client Event",
          color: "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300",
        };
      default:
        return {
          label: "Milestone",
          color: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300",
        };
    }
  };

  const getUrgencyBadge = (m: ProposalMilestone) => {
    const urgency = getMilestoneUrgency(m, now);
    switch (urgency) {
      case "completed":
        return {
          label: "Completed",
          color:
            "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
        };
      case "overdue":
        return {
          label: "Overdue",
          color:
            "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200 dark:border-rose-800 font-bold",
        };
      case "due_soon":
        return {
          label: "Due Soon (<48h)",
          color:
            "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800 font-semibold",
        };
      default:
        return {
          label: "On Track",
          color:
            "bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
        };
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. Countdown Hero & Progress Strip */}
      <div className="grid gap-4 md:grid-cols-12">
        {/* Countdown Timer Widget (5 cols) */}
        <div className="glass rounded-xl border border-slate-200/80 p-4 sm:p-5 dark:border-slate-800/80 md:col-span-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Submission Countdown
              </span>
              <span className="text-[11px] font-medium text-slate-400">
                Target: {targetSubmissionDate} {targetSubmissionTime}
              </span>
            </div>

            <div className="mt-3 flex items-baseline gap-2">
              <span
                className={`text-2xl sm:text-3xl font-black ${
                  countdown.isPast
                    ? "text-rose-600 dark:text-rose-400"
                    : countdown.totalHours <= 48
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-[#2ba5b5]"
                }`}
              >
                {countdown.label}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {countdown.isPast
                ? "The official proposal submission deadline has passed."
                : `Countdown to final client delivery for ${opportunity.client}.`}
            </p>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
            <span className="text-slate-400">Timezone: America/Chicago</span>
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {opportunity.dueDate ? `Due ${opportunity.dueDate}` : "No due date set"}
            </span>
          </div>
        </div>

        {/* Milestone Readiness & Next-Up Card (7 cols) */}
        <div className="glass rounded-xl border border-slate-200/80 p-4 sm:p-5 dark:border-slate-800/80 md:col-span-7 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Response Schedule Progress
              </span>
              <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                {completedCount} of {totalCount} completed ({progressPct}%)
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div
                className="h-full bg-[#2ba5b5] transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            {/* Next Milestone Box */}
            <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/80 p-3 dark:border-slate-800 dark:bg-slate-800/50">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-slate-500 uppercase">
                  Next Milestone
                </span>
                {nextMilestone && (
                  <span className="text-xs font-mono font-bold text-[#2ba5b5]">
                    {nextMilestone.targetDate} {nextMilestone.targetTime || ""}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
                {nextMilestone ? nextMilestone.title : "All scheduled milestones completed! 🎉"}
              </p>
              {nextMilestone?.ownerName && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
                  <User className="h-3 w-3" />
                  <span>Lead: {nextMilestone.ownerName}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between pt-2 text-[11px] text-slate-400">
            <span>Backward-scheduled from submission deadline</span>
            <span>Skips weekends (Mon-Fri)</span>
          </div>
        </div>
      </div>

      {/* 2. Recalculate / Sync Banner when due date changed */}
      {dueDateDrift && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200 shadow-xs">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600 shrink-0" />
            <div>
              <span className="font-bold">Proposal Due Date Changed</span>
              <p className="mt-0.5">
                The opportunity due date ({opportunity.dueDate}) differs from the current schedule.
                Recalculate internal gates to realign the team? (Your custom dates will be
                preserved).
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGenerateSchedule}
            disabled={isGenerating}
            className="btn-animate inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            <span>Recalculate Schedule</span>
          </button>
        </div>
      )}

      {actionError && (
        <div className="rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {actionError}
        </div>
      )}

      {/* 3. Toolbar & Controls */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 pb-3 dark:border-slate-800">
        <div className="flex flex-wrap items-center gap-2">
          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">All Categories</option>
            <option value="external_deadline">Client Deadlines</option>
            <option value="internal_gate">Internal Gates</option>
            <option value="client_event">Client Events</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <option value="all">All Status</option>
            <option value="pending">Open Milestones</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          {/* ICS Download */}
          <button
            type="button"
            onClick={handleDownloadIcs}
            disabled={milestones.length === 0}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 disabled:opacity-40"
            title="Download iCalendar file (.ics) for Outlook or Apple Calendar"
          >
            {copiedCal ? (
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            <span>Export (.ICS)</span>
          </button>

          {/* Auto-generate Button */}
          <button
            type="button"
            onClick={handleGenerateSchedule}
            disabled={isGenerating || !opportunity.dueDate}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 px-3 py-1.5 text-xs font-semibold text-[#2ba5b5] hover:bg-teal-100 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-300 disabled:opacity-50"
            title="Auto-calculate internal gates based on proposal due date"
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            <span>{milestones.length === 0 ? "Generate Schedule" : "Re-Sync Gates"}</span>
          </button>

          {/* Add Milestone */}
          <button
            type="button"
            onClick={() => {
              setEditingMilestone(null);
              setIsModalOpen(true);
            }}
            className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Milestone</span>
          </button>
        </div>
      </div>

      {/* 4. Chronological Timeline List */}
      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#2ba5b5]" />
        </div>
      ) : filteredMilestones.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center dark:border-slate-800">
          <Calendar className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
          <h4 className="mt-3 text-sm font-semibold text-slate-800 dark:text-slate-200">
            No Milestones Scheduled
          </h4>
          <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
            Build your proposal critical path by auto-generating backward-scheduled gates or adding
            custom deadlines.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={handleGenerateSchedule}
              disabled={isGenerating || !opportunity.dueDate}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-[#238a97]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Auto-Generate Backward Schedule</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMilestones.map((m) => {
            const catBadge = getCategoryBadge(m.category);
            const urgency = getUrgencyBadge(m);
            const gcalUrl = buildGoogleCalendarUrl(m, opportunity.title, opportunity.client);
            const isCompleted = m.status === "completed";

            return (
              <div
                key={m.id}
                className={`group relative flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border p-4 transition-all shadow-2xs ${
                  isCompleted
                    ? "border-slate-200 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-900/40 opacity-75"
                    : "border-slate-200/90 bg-white hover:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-900"
                }`}
              >
                {/* Left: Completion Toggle & Title */}
                <div className="flex items-start gap-3 min-w-0">
                  <button
                    type="button"
                    onClick={() => handleToggleComplete(m)}
                    className="mt-0.5 text-slate-400 hover:text-[#2ba5b5] transition-colors shrink-0"
                    title={isCompleted ? "Mark as pending" : "Mark as completed"}
                  >
                    {isCompleted ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <Circle className="h-5 w-5 hover:text-[#2ba5b5]" />
                    )}
                  </button>

                  <div className="space-y-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`text-sm font-bold ${
                          isCompleted
                            ? "line-through text-slate-500 dark:text-slate-400"
                            : "text-slate-900 dark:text-white"
                        }`}
                      >
                        {m.title}
                      </span>

                      <span
                        className={`rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase ${catBadge.color}`}
                      >
                        {catBadge.label}
                      </span>

                      <span
                        className={`rounded-md border px-2 py-0.5 text-[10px] uppercase ${urgency.color}`}
                      >
                        {urgency.label}
                      </span>

                      {m.compressed && (
                        <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          Compressed
                        </span>
                      )}
                    </div>

                    {m.description && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">
                        {m.description}
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-slate-400">
                      {m.ownerName && (
                        <span className="flex items-center gap-1 font-medium text-slate-600 dark:text-slate-300">
                          <User className="h-3 w-3 text-slate-400" />
                          <span>{m.ownerName}</span>
                        </span>
                      )}
                      {isCompleted && m.completedAt && (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          Completed {new Date(m.completedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right: Date, Time & Actions */}
                <div className="flex items-center justify-between sm:justify-end gap-3 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100 dark:border-slate-800">
                  <div className="flex flex-col sm:items-end text-xs">
                    <div className="flex items-center gap-1 font-mono font-bold text-slate-800 dark:text-slate-200">
                      <Calendar className="h-3.5 w-3.5 text-[#2ba5b5]" />
                      <span>{m.targetDate}</span>
                    </div>
                    {m.targetTime && (
                      <div className="flex items-center gap-1 text-[11px] text-slate-400">
                        <Clock className="h-3 w-3" />
                        <span>
                          {m.targetTime} ({m.timezone.split("/")[1] || "Central"})
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5">
                    {/* Add to Google Calendar */}
                    <a
                      href={gcalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-animate rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      title="Add to Google Calendar"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>

                    {/* Edit */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingMilestone(m);
                        setIsModalOpen(true);
                      }}
                      className="btn-animate rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      title="Edit milestone"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>

                    {/* Delete */}
                    <button
                      type="button"
                      onClick={() => handleDelete(m.id)}
                      className="btn-animate rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
                      title="Delete milestone"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 5. Create / Edit Modal */}
      {isModalOpen && (
        <MilestoneModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setEditingMilestone(null);
          }}
          milestone={editingMilestone}
          opportunityId={opportunity.id}
          onSave={handleSaveMilestone}
        />
      )}
    </div>
  );
};
